import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { trackLogin, saveUserToFirestore, resolveFinalEmail } from '../../utils';
import { AI_API_URL } from '../../constants';

/**
 * 네이버 인증 관련 로직을 담당하는 커스텀 훅
 * @param {function} setLoginDialogOpen - 로그인 다이얼로그 상태 설정 함수
 * @param {function} showAlert - AlertDialog 표시 함수
 * @param {function} setAuthLoading - 로딩 상태 설정 함수
 * @returns {object} - 네이버 인증 관련 함수들
 */
export const useNaverAuth = (setLoginDialogOpen, showAlert = null, setAuthLoading = null) => {
  /**
   * 네이버 OAuth 인증 처리 함수
   * @returns {Promise<boolean>} - 성공 여부
   */
  const handleNaverAuth = async () => {
    const width = 460;
    const height = 529;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    try {
      // 1. 백엔드에서 안전한 OAuth state 생성
      const stateResponse = await fetch(`${AI_API_URL}/auth/generate-state`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider: "naver" }),
      });

      if (!stateResponse.ok) {
        throw new Error('OAuth state 생성에 실패했습니다.');
      }

      const { state } = await stateResponse.json();
      
      // 2. state를 로컬 스토리지에 저장 (검증용)
      localStorage.setItem('oauth_state', state);

      return new Promise((resolve) => {
        let isCallbackProcessing = false;
        window.naverLoginCallback = async (code) => {
          if (isCallbackProcessing) return;
          isCallbackProcessing = true;
          
          // 네이버 인증 완료 즉시 모달 닫고 로딩 시작
          if (setLoginDialogOpen) {
          setLoginDialogOpen(false);
            console.log('🔒 네이버 로그인 모달 닫기 완료');
          }
          
          if (setAuthLoading) {
            setAuthLoading(true);
            console.log('🔄 네이버 로그인 로딩 시작');
          }
          
          try {
            // URL에서 state 파라미터 추출 (네이버 콜백 페이지에서 전달받음)
            const urlParams = new URLSearchParams(window.location.search);
            const returnedState = urlParams.get('state');
            
            // state 검증
            const storedState = localStorage.getItem('oauth_state');
            if (!returnedState || returnedState !== storedState) {
              throw new Error('보안 검증에 실패했습니다. 다시 시도해주세요.');
            }
            
            // state 사용 후 제거
            localStorage.removeItem('oauth_state');

            const response = await fetch(`${AI_API_URL}/firebase_custom_token/naver`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ code, state: returnedState }),
            });

          if (!response.ok) {
            const errorData = await response.text();
            console.error('Server response error:', errorData);
            throw new Error(`Firebase 커스텀 토큰 발급 실패: ${errorData}`);
          }

          const { firebaseToken, email, userInfo, merged } = await response.json();
          const userCredential = await signInWithCustomToken(auth, firebaseToken);
          
          if (merged) {
            console.log('✅ 기존 계정에 네이버 OAuth가 연동되었습니다!');
            
            // AlertDialog 또는 기본 alert 사용
            if (showAlert) {
              showAlert('success', '연동 완료', '기존 이메일 계정에 네이버 로그인이 연동되었습니다! 이제 네이버로도 로그인할 수 있습니다.');
            }
            
            // 계정 연동 시에는 기존 정보를 유지하면서 네이버 정보만 추가
            trackLogin('naver_linked');
          } else {
            // 최종 이메일 결정
            const finalEmail = await resolveFinalEmail(email, userCredential.user.uid, 'naver');
            
            // 네이버 로그인 정보와 함께 저장
            await saveUserToFirestore(userCredential.user, {
              loginMethod: 'naver',
              email: finalEmail,
              naverProfile: {
                ...userInfo
              }
            });
            
            // Google Analytics 로그인 이벤트 추적
            trackLogin('naver');
          }
          
          // 모든 처리 완료 후 로딩 종료
          if (setAuthLoading) {
            setAuthLoading(false);
            console.log('🔄 네이버 로그인 로딩 종료 (성공)');
          }
          
          resolve(true);
        } catch (e) {
          console.error('Error in naverLoginCallback:', e);
          
          // 로딩 종료
          if (setAuthLoading) {
            setAuthLoading(false);
            console.log('🔄 네이버 로그인 로딩 종료 (에러)');
          }
          
          if (showAlert) {
            showAlert('error', '네이버 로그인 실패', e.message);
          } else {
            alert("네이버 로그인 실패: " + e.message);
          }
          resolve(false);
        }
      };

        const naverLoginWindow = window.open(
          `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${import.meta.env.VITE_NAVER_CLIENT_ID}&redirect_uri=${encodeURIComponent(import.meta.env.VITE_NAVER_REDIRECT_URI)}&state=${state}&auth_type=reauthenticate`,
          'naverLogin',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        if (!naverLoginWindow) {
          if (showAlert) {
            showAlert('warning', '팝업 차단', '팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
          } else {
            alert("팝업이 차단되었습니다. 팝업 차단을 해제해주세요.");
          }
          resolve(false);
        }
      });
    } catch (error) {
      console.error('네이버 OAuth 초기화 실패:', error);
      if (showAlert) {
        showAlert('error', '네이버 로그인 오류', '로그인 초기화에 실패했습니다. 다시 시도해주세요.');
      } else {
        alert('로그인 초기화에 실패했습니다. 다시 시도해주세요.');
      }
      return false;
    }
  };

  return {
    handleNaverAuth
  };
}; 